/**
 * What the action key does here, and everything else it could do.
 *
 * One key, one thing, and the screen says which before you press it. This is
 * the module that decides which — and it decides nothing else: it is handed a
 * list of everything possible where the player is standing and hands back the
 * same list in an order, with the winner on the front. It does not know what a
 * cart is, it cannot pick anything up, and it has no opinion about the HUD.
 *
 * It exists because the ranking used to live in `main.js` as a chain of
 * `else if`, and that chain had two of them: one deciding who got the key press
 * and a second, written separately, deciding which line the screen showed. When
 * those two disagree the player presses a key that does something other than
 * what they read, and every one of the rules below was discovered by exactly
 * that happening — three times, each found by a benchmark coming back with a
 * bare ballroom rather than by anybody noticing.
 *
 * Now there is one order and both readings come off it, which is the only way
 * the promise at the top can be kept.
 *
 * ---------------------------------------------------------------------------
 * THE RULES THAT WERE PAID FOR
 *
 * Every one of these cost a wrong answer to find. They are tiers first, then
 * distance, and the three that are not about distance are the three that were
 * bought:
 *
 *   The job in your hands beats small talk. You are standing at a table you
 *   are dressing with an armful of chairs; the Captain wandering past is not
 *   what the key is for. `PLACING` sits above `NEAR`.
 *
 *   Concealment never eats the placement press. A round with a linen on it is
 *   a hiding place, so the moment the first table was dressed every table
 *   after it offered "get under this" and quietly ate the press meant to
 *   dress it. Half the room finished bare with the linens still in the cart.
 *   `POSSIBLE` — everything that is merely available where you stand — sits
 *   below `PLACING`.
 *
 *   A person and a cart do not outrank each other; whichever is nearer wins.
 *   Ranking the cart first meant a trolley parked four feet from the
 *   Housekeeping lead ate the press meant for her. Ranking the person first
 *   meant pushing a cart past a colleague dropped the handle for as long as
 *   you were beside them. Distance is the only answer that is not a special
 *   case, and it is what a person would do.
 *
 * And one more, which is a class rule and has to be, because distance cannot
 * express it: **a cart outranks the litter around it, always.** There are
 * oranges and candles all over the storeroom and a cart you walked up to on
 * purpose should not be shadowed by a candle that rolled under it. When it
 * was, the benchmark quietly stopped using the cart and went from 114 minutes
 * back to 151.
 * ---------------------------------------------------------------------------
 */

/**
 * The tiers, best first.
 *
 * A tier is "what kind of claim this has on you", and the order is the order a
 * person would answer in. Within a tier, distance decides.
 */
export const TALKING = 'talking';
export const FIXED = 'fixed';
export const PLACING = 'placing';
export const NEAR = 'near';
export const POSSIBLE = 'possible';

export const TIERS = Object.freeze([TALKING, FIXED, PLACING, NEAR, POSSIBLE]);

/**
 * The tier the press never reaches.
 *
 * `POSSIBLE` is everything that is merely available where you happen to be
 * standing rather than anything you walked up to — climbing the thing in front
 * of you, getting under the table, taking something out of your own pocket,
 * having a word with whoever is passing. They are real verbs and several are
 * good ones, and not one of them is what you meant by pressing a key while
 * standing in a corridor.
 *
 * So they are reachable only by holding it, which is the whole point of the
 * hold: the press does the thing you came here for and the hold has everything
 * else. It also means a press with nothing in front of you does nothing at all,
 * which is the correct answer and was not the old one — the old chain would
 * offer to climb a stack of crates because there was room in the prompt line.
 */
export const FAN_ONLY = Object.freeze(new Set([POSSIBLE]));

const ORDER = Object.freeze(Object.fromEntries(TIERS.map((tier, index) => [tier, index])));

/** Kinds within `NEAR` that the cart rule is about. */
export const CART = 'cart';
export const LITTER = 'litter';

export class PromptError extends Error {}

/**
 * One thing the key could do, as the screen will read it.
 *
 * `verb` and `what` are separate so the sentence is assembled in one place and
 * cannot drift into thirteen different phrasings — "pick up round", "get under
 * table", "ask Roz for linen", "ride cart". A candidate with no `what` is a
 * verb on its own, which a few of them are: "clock in", "sleep on it".
 */
export function describe(candidate) {
  if (!candidate) return null;
  return candidate.what ? `${candidate.verb} ${candidate.what}` : candidate.verb;
}

/** Throws on a candidate that could not be rendered or ranked, so a typo fails loudly. */
function check(candidate, index) {
  if (!candidate || typeof candidate !== 'object') {
    throw new PromptError(`candidate ${index} is not an object`);
  }
  if (!candidate.id) throw new PromptError(`candidate ${index} has no id`);
  if (!candidate.verb) throw new PromptError(`candidate ${candidate.id} has no verb`);
  if (!ORDER[candidate.tier] && ORDER[candidate.tier] !== 0) {
    throw new PromptError(`candidate ${candidate.id} has no tier`);
  }
  if (typeof candidate.run !== 'function' && !candidate.blocked) {
    throw new PromptError(`candidate ${candidate.id} does nothing and is not blocked`);
  }
}

/**
 * The cart rule, applied to a list already in distance order.
 *
 * A post-pass rather than a comparison, because as a comparison it is not one:
 * cart beats litter always, person beats cart by distance and litter beats
 * person by distance, and a cart five feet away with a candle at one foot and a
 * colleague at three makes a loop that no sort can resolve. Moving the cart up
 * past the litter it is standing among is what the rule actually says, and it
 * is a thing you can do to a list rather than a thing you can ask about a pair.
 */
function cartsBeforeLitter(list) {
  const cart = list.findIndex((candidate) => candidate.kind === CART);
  if (cart < 0) return list;
  const litter = list.findIndex((candidate) => candidate.kind === LITTER);
  if (litter < 0 || litter > cart) return list;
  const out = [...list];
  out.splice(litter, 0, ...out.splice(cart, 1));
  return out;
}

/**
 * Aiming: the one the stick or the camera points at, among the things in reach.
 *
 * Only inside `NEAR`, which is the one tier where different kinds of thing
 * compete (a person, a cart, something on the floor) and where distance alone
 * picked the one at your elbow when you were looking at the one in front of
 * you. `FIXED` is already decided by facing (`interaction.js`), and the tiers
 * stay first: aim never lifts a thing over the job in your hands.
 *
 * A candidate that says where it is (`at`, a point in the world) is scored as
 * its distance plus `weight` metres for every unit of (1 - cos) between the aim
 * and the way to it: dead ahead adds nothing, square to one side a metre,
 * straight behind two. The one already on top keeps `keep` metres in hand
 * (matched by `focus`, the thing's own identity), so two things at nearly the
 * same angle do not swap places every frame the stick wobbles. A candidate
 * with no `at` is scored on its distance as before.
 */
export const AIM = Object.freeze({ weight: 1, keep: 0.3 });

function aimed(candidate, aim) {
  const distance = candidate.distance ?? 0;
  if (!aim || candidate.tier !== NEAR || !candidate.at) return distance;
  const dx = candidate.at.x - aim.from.x;
  const dz = candidate.at.z - aim.from.z;
  const length = Math.hypot(dx, dz);
  let score = distance;
  if (length > 0.2) score += AIM.weight * (1 - (dx * aim.dir.x + dz * aim.dir.z) / length);
  if (aim.keep && candidate.focus && candidate.focus === aim.keep) score -= AIM.keep;
  return score;
}

/**
 * Everything available here, in the order it should be offered.
 *
 * Stable: candidates that tie on tier and distance come back in the order they
 * were handed over, so a caller that gathers in a fixed order gets a fixed
 * answer and a frame does not reshuffle under the player.
 *
 * `aim`, when given, is `{ from, dir, keep }`: where the player stands, the
 * unit direction on the floor they are aiming (the stick while it is pushed,
 * the camera otherwise) and the `focus` of the thing on top last frame.
 */
export function rank(candidates = [], aim = null) {
  const list = candidates.filter(Boolean);
  list.forEach(check);

  const sorted = list
    .map((candidate, index) => ({ candidate, index, score: aimed(candidate, aim) }))
    .sort((a, b) => {
      const tier = ORDER[a.candidate.tier] - ORDER[b.candidate.tier];
      if (tier !== 0) return tier;
      const gap = a.score - b.score;
      if (Math.abs(gap) > 1e-6) return gap;
      return a.index - b.index;
    })
    .map((entry) => entry.candidate);

  // The one class rule, applied inside the tier it is about.
  const near = sorted.filter((candidate) => candidate.tier === NEAR);
  if (near.length < 2) return sorted;
  const fixed = cartsBeforeLitter(near);
  let at = 0;
  return sorted.map((candidate) => (candidate.tier === NEAR ? fixed[at++] : candidate));
}

/**
 * What the key does, and what holding it would offer instead.
 *
 * `top` is the one thing a press performs and the one sentence the screen
 * shows. `rest` is everything else here, which is what the hold fans out. The
 * two never disagree about what is available, because there is only one list
 * and this is both ends of it.
 *
 * A blocked candidate can still be `top`. That is deliberate: "the clock will
 * not take a punch from somebody in their own clothes" is the most useful thing
 * the screen can say while you are standing in front of it in blacks, and
 * hiding it in favour of the next thing down would leave the player pressing a
 * key that does something else entirely.
 */
export function route(candidates = [], aim = null) {
  const all = rank(candidates, aim);
  const top = all.find((candidate) => !FAN_ONLY.has(candidate.tier)) ?? null;
  return { all, top, rest: all.filter((candidate) => candidate !== top) };
}

/**
 * The sentence for one candidate, with the key that performs it.
 *
 * `key` is passed in rather than hardcoded because which key that is belongs to
 * the bindings, and this module has never read a binding in its life.
 *
 * `key: null` is the sentence on its own, for a caller that draws the key
 * itself. The fan does: it puts a number in its own column against every row so
 * the numbers line up down the menu, and a bracket buried in the middle of a
 * string cannot be made to do that. Passing an empty string used to be how that
 * was asked for, and what it actually produced was a row reading "[]  take the
 * vape pen" with nothing in the bracket and no way to find out what went there.
 */
export function line(candidate, { key = 'E', blocked = null } = {}) {
  if (!candidate) return null;
  const said = describe(candidate);
  if (candidate.blocked) return `${said} · ${candidate.blocked}`;
  if (blocked) return `${said} · ${blocked}`;
  return key === null ? said : `[${key}]  ${said}`;
}

/**
 * Performs one, and says whether it went.
 *
 * The only place a candidate is ever run, so "the screen said it and the key
 * did it" is one call rather than a promise two hundred lines apart.
 */
export function perform(candidate) {
  if (!candidate) return { ok: false, reason: 'nothing here' };
  if (candidate.blocked) return { ok: false, reason: candidate.blocked };
  candidate.run();
  return { ok: true, id: candidate.id };
}
